// hooks/useMealplanning.js
import { useState, useEffect, useCallback } from "react";
import { fetchMealPlansApi, createMealPlanApi, updateMealPlanApi, deleteMealPlanApi, fetchMealFavoritesApi } from "../services/mealplanningService";

export function useMealplanning({ workspaceId, token }) {
  const [favorites, setFavorites] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ---------- GET ----------
  const fetchPlans = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError("");

    try {
      const data = await fetchMealPlansApi({ workspaceId, token });
      setPlans(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load meal plans.");
    } finally {
      setLoading(false);
    }
  }, [workspaceId, token]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // ---------- GET FAVORITES ----------
  const fetchFavorites = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const data = await fetchMealFavoritesApi({ workspaceId, token });
      setFavorites(data);
    } catch (err) {
      console.error("Failed to load favorites:", err);
    }
  }, [workspaceId, token]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // ---------- POST ----------
  const addPlan = async (plan) => {
    try {
      const newPlan = await createMealPlanApi({ token, plan });
      setPlans((prev) => [...prev, newPlan]);
    } catch (err) {
      console.error(err);
      setError("Failed to create meal plan.");
    }
  };

  // ---------- UPDATE ----------
  const updatePlan = async (id, updatedData) => {
    try {
      const updated = await updateMealPlanApi({ token, id, plan: updatedData });
      setPlans((prev) => prev.map((p) => (p.id === id ? updated : p)));
    } catch (err) {
      console.error(err);
      setError("Failed to update meal plan.");
    }
  };

  // ---------- SHIFT DATES ----------
  const shiftPlans = async (days, startDateStr = null) => {
    if (!plans || plans.length === 0) return;
    setLoading(true);
    try {
      // If startDate is provided, parse it. Otherwise use today.
      let filterDate = new Date();
      filterDate.setHours(0, 0, 0, 0);

      if (startDateStr) {
        const parsed = new Date(startDateStr);
        if (!isNaN(parsed.getTime())) {
          filterDate = parsed;
          // Ensure we reset time part just in case
          filterDate.setHours(0, 0, 0, 0);
        }
      }

      const updates = plans
        .filter(p => {
          const pDate = new Date(p.planDate);
          pDate.setHours(0, 0, 0, 0);
          return pDate >= filterDate;
        })
        .map(p => {
          const date = new Date(p.planDate);
          date.setDate(date.getDate() + days);
          const newDateStr = date.toISOString().split('T')[0];
          return updateMealPlanApi({ token, id: p.id, plan: { ...p, planDate: newDateStr } });
        });

      if (updates.length > 0) {
        await Promise.all(updates);
        await fetchPlans();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to shift plans.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- DELETE ----------
  const deletePlan = async (id) => {
    try {
      await deleteMealPlanApi({ token, id });
      setPlans((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err);
      setError("Failed to delete meal plan.");
    }
  };

  return {
    plans,
    loading,
    error,
    fetchPlans,
    addPlan,
    updatePlan,
    deletePlan,
    shiftPlans,
    favorites,
    setFavorites,
  };
}
